import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { LIST_PER_PAGE } from './useListPerPage';

const range = (from, to) => Array.from({ length: to - from + 1 }, (_, index) => from + index);

/**
 * Números das páginas: até 10, todos; acima disso, a primeira, as vizinhas da atual e a última.
 * Reticências só quando escondem mais de uma página.
 */
export function pageItems(page, total) {
  if (total <= 10) return range(1, total);
  const start = Math.max(2, Math.min(page - 2, total - 6));
  const end = Math.min(total - 1, Math.max(page + 2, 7));
  return [
    1,
    ...(start === 3 ? [2] : start > 3 ? ['gap-start'] : []),
    ...range(start, end),
    ...(end === total - 2 ? [total - 1] : end < total - 2 ? ['gap-end'] : []),
    total,
  ];
}

/** Rodapé das listas (5661): quantidade por página à esquerda e navegação à direita. */
export default function ListPagination({
  page,
  totalPages,
  perPage,
  summary,
  onPageChange,
  onPerPageChange,
}) {
  const total = Math.max(totalPages, 1);
  const go = (target) => target !== page && onPageChange(target);
  return (
    <footer className="tc-k-list-pagination">
      <div className="tc-k-per-page" role="group" aria-label="Visualizar por página">
        <span aria-hidden="true">Visualizar por página</span>
        <div className="tc-k-segmented">
          {LIST_PER_PAGE.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={value === perPage}
              onClick={() => value !== perPage && onPerPageChange(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </div>
      <span className="tc-k-list-summary">{summary}</span>
      <nav className="tc-k-pages" aria-label="Páginas da lista">
        <span>
          Página {page} de {total}
        </span>
        <button
          type="button"
          aria-label="Primeira página da lista"
          disabled={page <= 1}
          onClick={() => go(1)}
        >
          <ChevronsLeft size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Página anterior da lista"
          disabled={page <= 1}
          onClick={() => go(page - 1)}
        >
          <ChevronLeft size={15} aria-hidden="true" />
        </button>
        {pageItems(page, total).map((item) =>
          typeof item === 'number' ? (
            <button
              key={item}
              type="button"
              aria-current={item === page ? 'page' : undefined}
              onClick={() => go(item)}
            >
              {item}
            </button>
          ) : (
            <span key={item} aria-hidden="true">
              …
            </span>
          ),
        )}
        <button
          type="button"
          aria-label="Próxima página da lista"
          disabled={page >= total}
          onClick={() => go(page + 1)}
        >
          <ChevronRight size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Última página da lista"
          disabled={page >= total}
          onClick={() => go(total)}
        >
          <ChevronsRight size={15} aria-hidden="true" />
        </button>
      </nav>
    </footer>
  );
}
